/*
 * Keeps something on screen while it leaves: `mounted` stays true for `ms` after `open` turns
 * false, and `state` ("open" / "closed") drives its .motion-* exit. Opened again while leaving,
 * it turns back from where it is (CSS transitions start from the current value).
 */
import { useEffect, useState } from 'react'

export function usePresence(open: boolean, ms = 160): { mounted: boolean; state: 'open' | 'closed' } {
    const [mounted, setMounted] = useState(open)
    // Opened: on screen at once (state adjusted while rendering, not in an effect).
    if (open && !mounted) setMounted(true)
    useEffect(() => {
        if (open) return
        const timer = setTimeout(() => setMounted(false), ms)
        return () => clearTimeout(timer)
    }, [open, ms])
    return { mounted: open || mounted, state: open ? 'open' : 'closed' }
}

/**
 * The last `value` that wasn't null, kept for `ms` after it turns null: what a dialog opened for
 * one thing (a person, a role) still shows while it leaves.
 *
 *   const shown = useLingering(editing, 200)
 *   {shown && <Modal open={editing !== null} ...>{shown.name}</Modal>}
 */
export function useLingering<T>(value: T | null, ms = 200): T | null {
    const [kept, setKept] = useState<T | null>(value)
    if (value !== null && value !== kept) setKept(value)
    useEffect(() => {
        if (value !== null) return
        const timer = setTimeout(() => setKept(null), ms)
        return () => clearTimeout(timer)
    }, [value, ms])
    return value ?? kept
}
